import { signal } from '@preact/signals-react';
import type { IGroupSource } from 'AppointmentBox/ModalConfirmGroup';
import { ANYONE_ID } from 'Calendar/anyone';
import { IDatePickerValue } from 'components/DatePicker';
import moment from 'utils/moment';
import { IBodyApiQuickBooking } from 'services/types/quickBooking';
import { ICustomerUI } from 'types/customer';
import getTimeLines, { setTime } from 'utils/getTimeLines';
import uuid from 'utils/uuid';
import { customerSectionRef } from './Customer';
import React from 'react';
import { IQuickBookingGroupState, serviceNamesOf } from './quick-booking-group-payload';
const openSignal = signal<boolean>(false);
const openDropDownCs = signal<boolean>(false);
const staffs = signal<string[]>([]);
const requestStaff = signal<boolean>(false);
const currentDate = signal<IDatePickerValue | null>(moment());
const startTime = signal<string>('');
const note = signal<string>('');
const callerId = signal<string>('');
const duration = signal<number>(15);
const totalPeople = signal<number>(1);
const customer = signal<ICustomerUI | null>(null);
/** Chu ky lap (so ngay). null = khong lap — mac dinh, repeat phai la hanh dong co chu y. */
const repeatDayRepeat = signal<number | null>(null);
/** Chi so dong staff dang bi bao thieu dich vu; null = khong dong nao bi canh bao. */
const serviceWarningRow = signal<number | null>(null);


export type IQuickServiceItem = {
  value: string;
  label: any;
  id: number | string;
}
const services = signal<IQuickServiceItem[]>([]);
const uuidKey = signal<string>(uuid());

export type IQuickBookStaffSelected = {
  id: string;
  requestStaff?: boolean;
  services: IQuickServiceItem[];
};

const _timeLines = getTimeLines(setTime(9, 0), setTime(21, 0), 15).map(o => ({
  value: o.value.format('hh:mm A'),
  label: o.value.format('hh:mm A'),
}));
const timelines = signal<{
  value: string;
  label: string;
}[]>(_timeLines);

export interface IQuickBookRef {
  initFormService: (data: IQuickBookStaffSelected[]) => void;
}
export const quickbookRef = React.createRef<IQuickBookRef>();

// const getAvailableTimeslotByStaff = async (params: { currentDate: string, staffId: string }) => {
//   if (!params.currentDate || !params.staffId) return;
//   try {
//     const res: AxiosResponse<{ payload: { timeSlots: string[] } }> = await apis.getAvailableTimeSlotByStaff(params);
//     if (res.data.payload.timeSlots) {
//       const timeslots = res.data.payload.timeSlots;
//       timelines.value = timeslots.map(o => ({ value: o, label: o }));
//       if (!timeslots.includes(startTime.value)) {
//         startTime.value = first(timeslots) || '';
//       }
//     } else {
//       timelines.value = [];
//     }
//   } catch (error) { }
// };

// effect(() => {
//   const params = {
//     currentDate: currentDate.value?.format('MM-DD-YYYY') || '',
//     staffId: staffs.value[0] || '',
//   };
//   getAvailableTimeslotByStaff(params);
// });

/** Ngay va gio dang chon, gop lai thanh mot moc thoi gian duy nhat cho moi noi doc. */
const getStartMoment = () =>
  moment(`${currentDate.value?.format('MM-DD-YYYY')} ${startTime.value}`, 'MM-DD-YYYY hh:mm A');

type IQuickBookOpenExtra = {
  requestStaff?: boolean;
  customer?: ICustomerUI;
  callerId?: string;
};

/**
 * Reset toan bo state cua modal roi bom san danh sach dong staff.
 *
 * `rows` rong nghia la mo modal chua co staff nao (luong FULL_SCREEN) — luc do `staffs` cung
 * phai rong that su, khong duoc la `['']`, vi cac section khac doc `staffs.value[0]` de biet
 * da chon staff hay chua.
 */
const openWithRows = (rows: IQuickBookStaffSelected[], _startTime: string, additionalData?: IQuickBookOpenExtra) => {
  const currentDateValue = moment(_startTime);
  openSignal.value = true;
  currentDate.value = currentDateValue;
  startTime.value = currentDateValue.format('hh:mm A');
  duration.value = 60;
  staffs.value = rows.map(o => o.id);
  requestStaff.value = additionalData?.requestStaff || false;
  customer.value = additionalData?.customer || null;
  uuidKey.value = uuid();
  services.value = [];
  note.value = '';
  repeatDayRepeat.value = null;
  totalPeople.value = 1;
  callerId.value = additionalData?.callerId || '';
  timelines.value = _timeLines;
  serviceWarningRow.value = null;
  quickbookRef.current?.initFormService(rows);
  if (!customer.value) {
    setTimeout(() => {
      customerSectionRef.current?.focus();
    }, 500);
  }
};

const qbSignal = {
  openSignal,
  requestStaff,
  customer,
  startTime,
  duration,
  staffs,
  totalPeople,
  note,
  repeatDayRepeat,
  serviceWarningRow,
  services,
  currentDate,
  timelines,
  uuidKey,
  openDropDownCs,
  callerId,
  open: (staffId: string, _startTime: string, additionalData?: IQuickBookOpenExtra) => openWithRows([{
    id: staffId,
    requestStaff: additionalData?.requestStaff || false,
    services: [],
  }], _startTime, additionalData),
  /** Mo modal khong co dong staff nao — user tu them trong form (luong FULL_SCREEN). */
  openWithoutStaff: (_startTime: string, additionalData?: IQuickBookOpenExtra) => openWithRows([], _startTime, additionalData),
  close: () => {
    openSignal.value = false;
  },
  /**
   * Khung gio dung de hoi danh sach tho ranh.
   *
   * Cong phut ngay tren Moment roi moi dinh dang. Dinh dang truoc roi parse lai chuoi bang
   * `moment(chuoi)` khong kem format se roi ve `new Date()` theo mui gio may, trong khi mac dinh
   * cua thu vien da duoc dat sang mui gio tiem — hai mui lech nhau thi gio ket thuc rot ra truoc
   * gio bat dau, va server coi khung dao nguoc la khong trung lich voi ai nen tra ve gan nhu moi
   * tho, ke ca nguoi dang ban.
   */
  getCurrentStartEndTime: () => {
    const start = getStartMoment();
    return ({
      startTime: start.format('MM-DD-YYYY HH:mm'),
      endTime: start.clone().add(duration.value, 'minute').format('MM-DD-YYYY HH:mm'),
    });
  },
  /**
   * State cua Quick Booking, dich sang shape ma modal gan tho doc duoc.
   *
   * Nhom chi mo duoc khi form co dung mot dong staff, nen dich vu va Request Staff cua ca
   * nhom deu lay tu dong do.
   */
  getGroupSource: (row: IQuickBookStaffSelected): IGroupSource => ({
    customer: customer.value,
    time: getStartMoment(),
    durationMinutes: duration.value,
    serviceNames: serviceNamesOf(row.services),
    preferredStaffId: row.id,
    defaultRequestStaff: !!row.requestStaff,
  }),
  /** Phan state dung chung cho moi lich trong nhom, de dung payload dat lich. */
  getGroupState: (row: IQuickBookStaffSelected): IQuickBookingGroupState => ({
    customerId: parseInt(customer.value?.id || '') || '',
    duration: duration.value,
    startTime: getStartMoment().format('MM/DD/YYYY HH:mm'),
    note: note.value || '',
    callerId: callerId.value || '',
    services: row.services,
  }),
  getValues: (staffsSelected: IQuickBookStaffSelected[]) => {
    const _startTime = getStartMoment().format('MM/DD/YYYY HH:mm');
    return staffsSelected.map(o => ({
      customerId: parseInt(customer.value?.id || '') || '',
      bookingType: 0,
      duration: duration.value,
      isAnybody: o.id === ANYONE_ID ? 1 : 0,
      note: totalPeople.value > 1 ? `Group ${totalPeople.value}: ${note.value || ''}` : (note.value || ''),
      requestStaff: o.requestStaff,
      services: o.services.map(s => ({
        id: s.id || 0,
        serviceName: s.label,
        price: 0,
      })),
      staffId: parseInt(o.id),
      startTime: _startTime,
      totalPeople: totalPeople.value || 1,
      callerId: callerId.value || '',
    } as IBodyApiQuickBooking));
  },
};

export default qbSignal;
